import { useState, useEffect, useMemo, useRef } from 'react'
import { X, Plus, Trash2, Star, AlertCircle } from 'lucide-react'
import { TOKENS, renderTemplate, unknownTokens } from './templates'

// Title / description template builder. Templates are per league; one is the
// default the Schedule dialog starts from. Click a token chip to insert it at
// the caret; the preview renders against a real game from the current season
// so you see the actual output, not the placeholders.
//
// props: league, templates[], sampleGames[] (games to preview against),
//        ctxFor(game) → token map, api, onChange(templates), onClose
export default function TemplateBuilder({ league, templates, sampleGames, ctxFor, api, onChange, onClose }) {
  const [activeId, setActiveId] = useState(() => (templates.find(t => t.is_default) || templates[0])?.id ?? 'new')
  const [name, setName] = useState('')
  const [title, setTitle] = useState('')
  const [desc, setDesc] = useState('')
  const [sampleId, setSampleId] = useState(sampleGames[0]?.game_id || '')
  const [focus, setFocus] = useState('title')     // which field the next chip inserts into
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const titleRef = useRef(null)
  const descRef = useRef(null)

  const active = useMemo(() => templates.find(t => t.id === activeId) || null, [templates, activeId])

  // Load the selected template into the editor (or blank for "new").
  useEffect(() => {
    if (active) { setName(active.name); setTitle(active.title_template); setDesc(active.description_template || '') }
    else { setName(''); setTitle(''); setDesc('') }
    setErr('')
  }, [active])

  useEffect(() => {
    if (!sampleGames.find(g => g.game_id === sampleId)) setSampleId(sampleGames[0]?.game_id || '')
  }, [sampleGames, sampleId])

  const sample = sampleGames.find(g => g.game_id === sampleId) || null
  const ctx = sample ? ctxFor(sample) : null
  const dirty = active ? (name !== active.name || title !== active.title_template || desc !== (active.description_template || '')) : (name || title || desc)
  const bad = [...unknownTokens(title), ...unknownTokens(desc)]

  function insert(tokenKey) {
    const ref = focus === 'desc' ? descRef : titleRef
    const set = focus === 'desc' ? setDesc : setTitle
    const val = focus === 'desc' ? desc : title
    const el = ref.current
    const tok = `<${tokenKey}>`
    if (!el) { set(val + tok); return }
    const s = el.selectionStart ?? val.length, e = el.selectionEnd ?? val.length
    const next = val.slice(0, s) + tok + val.slice(e)
    set(next)
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(s + tok.length, s + tok.length) })
  }

  async function save(asDefault = false) {
    if (!name.trim() || !title.trim()) { setErr('Name and title template are required'); return }
    setBusy(true); setErr('')
    try {
      const patch = { name: name.trim(), title_template: title, description_template: desc, ...(asDefault ? { is_default: true } : {}) }
      let saved
      if (active) ({ template: saved } = await api.updateTemplate(active.id, patch))
      else ({ template: saved } = await api.createTemplate({ league, ...patch, is_default: asDefault || templates.length === 0 }))
      const { templates: list } = await api.listTemplates(league)
      onChange(list)
      setActiveId(saved.id)
    } catch (e) {
      setErr(e.body?.error || e.message)
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!active || !window.confirm(`Delete template "${active.name}"?`)) return
    setBusy(true)
    try {
      await api.deleteTemplate(active.id)
      const { templates: list } = await api.listTemplates(league)
      onChange(list)
      setActiveId((list.find(t => t.is_default) || list[0])?.id ?? 'new')
    } catch (e) {
      setErr(e.body?.error || e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="ts-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="ts-modal ts-modal-wide" role="dialog" aria-modal="true" aria-labelledby="ts-tpl-title">
        <div className="ts-modal-head">
          <h2 id="ts-tpl-title">Title &amp; description templates <span className="muted">· {league}</span></h2>
          <button type="button" className="ts-iconbtn" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>

        <div className="ts-tpl-body">
          {/* template list */}
          <aside className="ts-tpl-list" aria-label="Templates">
            {templates.map(t => (
              <button
                key={t.id} type="button"
                className={`ts-tpl-item ${t.id === activeId ? 'is-active' : ''}`}
                onClick={() => setActiveId(t.id)}
              >
                <span>{t.name}</span>
                {t.is_default ? <span className="ts-tpl-default" title="Default for this league"><Star size={11} /> default</span> : null}
              </button>
            ))}
            <button type="button" className={`ts-tpl-item ts-tpl-new ${activeId === 'new' ? 'is-active' : ''}`} onClick={() => setActiveId('new')}>
              <Plus size={13} /> New template
            </button>
          </aside>

          {/* editor */}
          <div className="ts-tpl-editor">
            <div className="form-group">
              <label htmlFor="ts-tpl-name">Template name</label>
              <input id="ts-tpl-name" value={name} onChange={e => setName(e.target.value)} placeholder="Default" />
            </div>

            <div className="ts-tpl-tokens" role="group" aria-label="Insert token">
              <span className="muted">Insert into {focus === 'desc' ? 'description' : 'title'}:</span>
              {TOKENS.map(t => (
                <button key={t.key} type="button" className="ts-chip ts-token" onClick={() => insert(t.key)} title={`<${t.key}> → ${ctx ? (ctx[t.key] || '—') : t.hint}`}>
                  {t.label}
                </button>
              ))}
            </div>

            <div className="form-group">
              <label htmlFor="ts-tpl-title-tpl">Title</label>
              <textarea
                id="ts-tpl-title-tpl" ref={titleRef} rows={2} value={title}
                onChange={e => setTitle(e.target.value)} onFocus={() => setFocus('title')}
                placeholder="<date> - <away-team> vs <home-team> - <season-years> <league> …"
              />
            </div>
            <div className="form-group">
              <label htmlFor="ts-tpl-desc-tpl">Description</label>
              <textarea
                id="ts-tpl-desc-tpl" ref={descRef} rows={3} value={desc}
                onChange={e => setDesc(e.target.value)} onFocus={() => setFocus('desc')}
                placeholder="<time> - <away-team> travels to <venue> to take on <home-team> …"
              />
            </div>

            {bad.length > 0 && (
              <p className="ts-tpl-warn"><AlertCircle size={13} /> Unknown token{bad.length > 1 ? 's' : ''}: {bad.map(b => `<${b}>`).join(', ')} — will be left as typed.</p>
            )}

            {/* preview */}
            <div className="ts-tpl-preview">
              <div className="ts-tpl-preview-head">
                <span className="muted">Preview with</span>
                <select value={sampleId} onChange={e => setSampleId(e.target.value)} aria-label="Preview game" disabled={!sampleGames.length}>
                  {!sampleGames.length && <option value="">No games synced yet</option>}
                  {sampleGames.map(g => (
                    <option key={g.game_id} value={g.game_id}>{g.game_date} · {g.visitor} at {g.home}</option>
                  ))}
                </select>
              </div>
              {ctx ? (
                <>
                  <div className="ts-tpl-out ts-tpl-out-title">{renderTemplate(title, ctx) || <span className="muted">— empty title —</span>}</div>
                  <div className="ts-tpl-out">{renderTemplate(desc, ctx) || <span className="muted">— empty description —</span>}</div>
                </>
              ) : <p className="muted">Sync a season to preview against real games.</p>}
            </div>

            {err && <p className="ts-tpl-warn"><AlertCircle size={13} /> {err}</p>}

            <div className="ts-modal-actions">
              <button type="button" onClick={() => save(false)} disabled={busy || !dirty}>{busy ? 'Saving…' : active ? 'Save' : 'Create'}</button>
              {!active?.is_default && (
                <button type="button" onClick={() => save(true)} disabled={busy}><Star size={13} /> {active ? 'Save as default' : 'Create as default'}</button>
              )}
              {active && <button type="button" className="danger" onClick={remove} disabled={busy}><Trash2 size={13} /> Delete</button>}
              <span className="ts-spacer" />
              <button type="button" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
